import { Container, Graphics, Rectangle, Sprite } from 'pixi.js';
import type { Application } from 'pixi.js';
import type { RawPiece } from '@blind-kings/game-core';
import { PIECE_VALUES } from '@blind-kings/game-core';
import type { LastAction } from '../../../characters';
import { loadBoardTexture, preloadCoreAssets, preloadMatchAssets } from './assets';
import { ActionAnimator } from './ActionAnimator';
import {
  Highlights,
  cellOrigin,
  screenToBoardCell,
  visualGridSize,
  type CellMark,
} from './Highlights';
import { PieceNode } from './PieceNode';
import type { TeamPalette } from '../../../player-colors';

export type BoardViewOptions = {
  app: Application;
  cols?: number;
  rows?: number;
  displayPieceValues?: boolean;
  interactive?: boolean;
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
  onCellPointer?: (cell: { x: number; y: number }, pieceId?: number) => void;
  /** When set, only these piece ids accept hover/pointer cursor. null = all interactive pieces. */
  hoverablePieceIds?: Set<number> | null;
};

export class BoardView {
  readonly root = new Container();
  private bg = new Sprite();
  private gridLines = new Graphics();
  private highlights = new Highlights();
  private piecesLayer = new Container();
  private pieceNodes = new Map<number, PieceNode>();
  private cellSize = 0;
  private cols: number;
  private rows: number;
  private lastLayoutWidth = 0;
  private lastLayoutHeight = 0;
  private renderVertically = false;
  private displayPieceValues: boolean;
  private board: RawPiece[][] = [];
  private selectedPieceId?: number;
  private pendingFlipPieceId?: number;
  private marks: CellMark[] = [];
  private suppressMarks = false;
  private animator: ActionAnimator;
  private ready = false;
  private teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
  private hoverablePieceIds: Set<number> | null = null;
  private cellPointerBound = false;
  private wiredPieceIds = new Set<number>();

  constructor(private opts: BoardViewOptions) {
    this.cols = opts.cols ?? 8;
    this.rows = opts.rows ?? 4;
    this.displayPieceValues = opts.displayPieceValues ?? true;
    this.teamPalettes = opts.teamPalettes;
    this.hoverablePieceIds = opts.hoverablePieceIds ?? null;
    const interactive = opts.interactive !== false;

    this.root.eventMode = interactive ? 'static' : 'none';
    this.bg.eventMode = 'none';
    this.gridLines.eventMode = 'none';
    this.piecesLayer.eventMode = interactive ? 'passive' : 'none';

    this.root.addChild(this.bg);
    this.root.addChild(this.gridLines);
    this.root.addChild(this.highlights);
    this.root.addChild(this.piecesLayer);
    opts.app.stage.addChild(this.root);

    this.animator = new ActionAnimator({
      piecesLayer: this.piecesLayer,
      getPieceNode: (id) => this.pieceNodes.get(id),
      getLayout: () => ({
        cellSize: this.cellSize,
        cols: this.cols,
        rows: this.rows,
        renderVertically: this.renderVertically,
      }),
      rebuildFromBoard: async (board) => {
        this.board = board;
        await this.rebuildPieces();
      },
    });

    if (interactive) this.bindCellPointer();

    opts.app.ticker.add(this.onTick);
  }

  private bindCellPointer() {
    if (this.cellPointerBound) return;
    this.cellPointerBound = true;
    this.root.on('pointertap', (e) => {
      if (this.animator.isAnimating) return;
      const local = this.root.toLocal(e.global);
      const cell = screenToBoardCell(
        local.x,
        local.y,
        this.cellSize,
        this.cols,
        this.rows,
        this.renderVertically
      );
      if (!cell) return;
      const piece = this.board[cell.y]?.[cell.x];
      this.opts.onCellPointer?.(cell, piece?.id);
    });
  }

  get isAnimating() {
    return this.animator.isAnimating;
  }

  async init() {
    await preloadCoreAssets();
    this.bg.texture = await loadBoardTexture();
    this.ready = true;
    // Action strips are only needed once play starts, so warm them off the
    // critical path. The match reveal usually gets here first.
    void preloadMatchAssets(this.teamPalettes);
  }

  destroy() {
    this.opts.app.ticker.remove(this.onTick);
    this.animator.cancel();
    for (const node of this.pieceNodes.values()) {
      node.destroy({ children: true, texture: false, textureSource: false });
    }
    this.pieceNodes.clear();
    this.root.destroy({ children: true, texture: false, textureSource: false });
  }

  setSkinId(skinId: string) {
    this.animator.setSkinId(skinId);
  }

  setOrientation(renderVertically: boolean) {
    this.renderVertically = renderVertically;
  }

  setTeamPalettes(teamPalettes?: Partial<Record<0 | 1, TeamPalette>>) {
    this.teamPalettes = teamPalettes;
  }

  setInteractive(interactive: boolean) {
    this.opts.interactive = interactive;
    this.root.eventMode = interactive ? 'static' : 'none';
    this.piecesLayer.eventMode = interactive ? 'passive' : 'none';
    if (interactive) this.bindCellPointer();
    for (const node of this.pieceNodes.values()) {
      if (interactive) this.wirePiecePointer(node);
      if (!interactive) node.setHovered(false);
      // Pieces stay clickable whenever the board is interactive; hover is gated separately.
      node.eventMode = interactive ? 'static' : 'none';
      node.cursor = interactive && this.isPieceHoverable(node.pieceId) ? 'pointer' : 'default';
    }
  }

  setHoverablePieceIds(ids: Set<number> | null) {
    this.hoverablePieceIds = ids;
    const interactive = this.opts.interactive !== false;
    for (const node of this.pieceNodes.values()) {
      if (!interactive) {
        node.setHovered(false);
        node.eventMode = 'none';
        node.cursor = 'default';
        continue;
      }
      const canHover = this.isPieceHoverable(node.pieceId);
      if (!canHover) node.setHovered(false);
      node.eventMode = 'static';
      node.cursor = canHover ? 'pointer' : 'default';
    }
  }

  private isPieceHoverable(pieceId: number) {
    if (this.opts.interactive === false) return false;
    if (!this.hoverablePieceIds) return true;
    return this.hoverablePieceIds.has(pieceId);
  }

  setDisplayPieceValues(show: boolean) {
    if (this.displayPieceValues === show) return;
    this.displayPieceValues = show;
    for (const node of this.pieceNodes.values()) {
      node.setDisplayValue(show, this.cellSize);
    }
  }

  async setBoard(
    board: RawPiece[][],
    options: {
      renderVertically: boolean;
      selectedPieceId?: number;
      pendingFlipPieceId?: number;
      marks?: CellMark[];
      force?: boolean;
    }
  ) {
    if (!this.ready) return;
    if (this.animator.isAnimating && !options.force) {
      this.selectedPieceId = options.selectedPieceId;
      this.pendingFlipPieceId = options.pendingFlipPieceId;
      this.marks = options.marks ?? [];
      this.redrawHighlights();
      this.refreshSelection();
      return;
    }

    const nextRows = board.length;
    const nextCols = board[0]?.length ?? 0;
    const sizeChanged = nextRows !== this.rows || nextCols !== this.cols;
    const orientationChanged = this.renderVertically !== options.renderVertically;

    this.board = board;
    this.rows = nextRows;
    this.cols = nextCols;
    this.renderVertically = options.renderVertically;
    this.selectedPieceId = options.selectedPieceId;
    this.pendingFlipPieceId = options.pendingFlipPieceId;
    this.marks = options.marks ?? [];

    if (
      (sizeChanged || orientationChanged) &&
      this.lastLayoutWidth > 0 &&
      this.lastLayoutHeight > 0
    ) {
      this.layoutToSize(this.lastLayoutWidth, this.lastLayoutHeight);
      return;
    }

    this.layout();
    await this.rebuildPieces();
    this.redrawHighlights();
  }

  layoutToSize(width: number, height: number) {
    if (width <= 0 || height <= 0) return;
    this.lastLayoutWidth = width;
    this.lastLayoutHeight = height;
    const { visualCols, visualRows } = visualGridSize(this.cols, this.rows, this.renderVertically);
    this.cellSize = Math.min(width / visualCols, height / visualRows);
    const boardW = this.cellSize * visualCols;
    const boardH = this.cellSize * visualRows;
    this.root.position.set((width - boardW) / 2, (height - boardH) / 2);
    this.layout();
    void this.rebuildPieces();
    this.redrawHighlights();
  }

  async playAction(action: LastAction, settleBoard: RawPiece[][]) {
    this.suppressMarks = true;
    this.redrawHighlights();
    try {
      await this.animator.play(action, settleBoard);
    } finally {
      this.suppressMarks = false;
      this.redrawHighlights();
    }
  }

  private layout() {
    const { visualCols, visualRows } = visualGridSize(this.cols, this.rows, this.renderVertically);
    const boardW = this.cellSize * visualCols;
    const boardH = this.cellSize * visualRows;
    this.bg.width = boardW;
    this.bg.height = boardH;
    // Empty cells have no sprites; root must own a hit area so they are clickable.
    this.root.hitArea = new Rectangle(0, 0, boardW, boardH);

    this.gridLines.clear();
    this.gridLines.setStrokeStyle({ width: 1, color: 0x000000, alpha: 0.55 });
    for (let c = 0; c <= visualCols; c++) {
      const x = c * this.cellSize;
      this.gridLines.moveTo(x, 0).lineTo(x, boardH);
    }
    for (let r = 0; r <= visualRows; r++) {
      const y = r * this.cellSize;
      this.gridLines.moveTo(0, y).lineTo(boardW, y);
    }
    this.gridLines.stroke();
  }

  private async rebuildPieces() {
    const keep = new Set<number>();
    const tasks: Promise<void>[] = [];

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = this.board[y]?.[x];
        if (!cell || cell.value === PIECE_VALUES.Empty) continue;
        const id = cell.id ?? y * this.cols + x;
        keep.add(id);

        let node = this.pieceNodes.get(id);
        if (!node) {
          const interactive = this.opts.interactive !== false;
          node = new PieceNode({
            id,
            value: cell.value,
            teamIndex: cell.teamIndex,
            cellSize: this.cellSize,
            displayValue: this.displayPieceValues,
            interactive,
            teamPalettes: this.teamPalettes,
          });
          // Always bind once; handlers gate on interactive / hoverablePieceIds.
          this.wirePiecePointer(node);
          this.pieceNodes.set(id, node);
          this.piecesLayer.addChild(node);
        } else if (
          node.pieceValue !== cell.value ||
          node.teamIndex !== cell.teamIndex ||
          node.needsRetint(this.teamPalettes)
        ) {
          tasks.push(
            node.setPieceIdentity(cell.value, cell.teamIndex, this.cellSize, this.teamPalettes)
          );
        } else {
          node.layoutToCellSize(this.cellSize);
        }

        const pos = cellOrigin(x, y, this.cellSize, this.cols, this.rows, this.renderVertically);
        node.position.set(pos.centerX, pos.centerY);
        node.setPulsing(this.pendingFlipPieceId === id);
        node.setSelected(this.selectedPieceId === id, this.cellSize);
        const interactive = this.opts.interactive !== false;
        const canHover = this.isPieceHoverable(id);
        node.eventMode = interactive ? 'static' : 'none';
        node.cursor = interactive && canHover ? 'pointer' : 'default';
        if (!canHover || !interactive) node.setHovered(false);
      }
    }

    for (const [id, node] of this.pieceNodes) {
      if (!keep.has(id)) {
        node.destroy({ children: true, texture: false, textureSource: false });
        this.pieceNodes.delete(id);
        this.wiredPieceIds.delete(id);
      }
    }

    await Promise.all(tasks);
  }

  private wirePiecePointer(node: PieceNode) {
    if (this.wiredPieceIds.has(node.pieceId)) return;
    this.wiredPieceIds.add(node.pieceId);
    node.on('pointerover', () => {
      if (this.animator.isAnimating) return;
      if (!this.isPieceHoverable(node.pieceId)) return;
      node.setHovered(true);
    });
    node.on('pointerout', () => node.setHovered(false));
    node.on('pointerdown', () => {
      if (this.animator.isAnimating) return;
      if (!this.isPieceHoverable(node.pieceId)) return;
      node.flashPress();
    });
    node.on('pointertap', (e) => {
      e.stopPropagation();
      if (this.animator.isAnimating) return;
      // Clicks always select when the board is interactive; hover gating is visual only.
      if (this.opts.interactive === false) return;
      for (let y = 0; y < this.rows; y++) {
        for (let x = 0; x < this.cols; x++) {
          const cell = this.board[y]?.[x];
          if (cell?.id === node.pieceId) {
            this.opts.onCellPointer?.({ x, y }, node.pieceId);
            return;
          }
        }
      }
      this.opts.onCellPointer?.({ x: 0, y: 0 }, node.pieceId);
    });
  }

  private visibleMarks() {
    if (this.suppressMarks || this.animator.isAnimating) return [];
    return this.marks;
  }

  private redrawHighlights() {
    this.highlights.draw(
      this.visibleMarks(),
      this.cellSize,
      this.cols,
      this.rows,
      this.renderVertically
    );
  }

  private refreshSelection() {
    for (const [id, node] of this.pieceNodes) {
      node.setSelected(this.selectedPieceId === id, this.cellSize);
      node.setPulsing(this.pendingFlipPieceId === id);
    }
  }

  private onTick = () => {
    const dt = this.opts.app.ticker.deltaMS;
    for (const node of this.pieceNodes.values()) {
      node.tickPulse(dt);
      node.tickAnim(dt);
    }
  };
}
